<script setup>
// import PartnershipsMapChartAmount from "@/components/charts/PartnershipsMapChart.vue";
import PartnershipsMap from "./PartnershipsMap.vue";
import Multiselect from "vue-multiselect";
import Vue, {computed, onMounted, provide, ref, watch} from "vue";
import marketsService from "@/services/markets.service";
import ReportModal from "@/views/market/ReportModal.vue";
import AddToTrackers from "@/components/manage/SelectTrackersModal.vue";
import trackersCompaniesService from "@/services/trackers-companies.service";
import {getNoun} from "@/helpers/string";
import MarketStartupsModal from "@/views/market/MarketStartupsModal.vue";
import SimilarStartups from "@/components/startups/SimilarStartups.vue";
import PartnershipsModal from "@/views/market/PartnershipsModal.vue";
import PartnershipsGraph from "@/views/market/PartnershipsGraph.vue";
import MarketUpdates from "@/views/market/MarketUpdates.vue";
import startupsService from "@/services/startups.service";
import {useRoute} from "vue2-helpers/vue-router";
import MarketReportsHistory from "@/views/market/MarketReportsHistory.vue";
import TrackersSelect from "@/components/manage/TrackersSelect.vue";
import {useStore} from "vue2-helpers/vuex";
import SelectCorporatesModal from "@/views/market/SelectCorporatesModal.vue";
import SelectStartupsModal from "@/views/market/SelectStartupsModal.vue";

const route = useRoute();
const store = useStore();
const clusterId = route.query.cluster_id || null;
const sectorId = route.query.sector_id || null;

const combinedList = ref([]);
const currentSelection = ref(null);
const currentSubsector = ref(null);
const currentSector = ref(null);
const viewLevel = ref(1);
const reportModal = ref(null);
const combinedSelect = ref(null);
const searchFilter = ref('');

const addToTrackersModal = ref(null);
const startupsModal = ref(null);
const similarStartupsModal = ref(null);
const partnershipsModal = ref(null);
const selectCorporatesModal = ref(null);
const selectStartupsModal = ref(null);

const userReportsHistory = ref(null);
const showSectorSelect = ref(false);
const hasSubscription = ref(false);
const showTrackerSelect = ref(false);

const counts = ref({});
const startupsIds = ref([]);
const startupsNodes = ref(false);
const excludeTrackers = ref([]);
const selectedTracker = ref(null);
const selectedCorporates = ref([]);
const selectedStartups = ref([]);
const searchType = ref('');

const searchObjectId = computed(() => {
    if (searchType.value === 'subsector') {
        return currentSubsector.value?.id;
    } else if (searchType.value === 'tracker') {
        return selectedTracker.value?.id;
    } else if (searchType.value === 'corporates') {
        return selectedCorporates.value.map(item => item.id);
    } else if (searchType.value === 'startups') {
        return selectedStartups.value.map(item => item.id);
    } else {
        return null;
    }
})

const sectorsList = computed(() => combinedList.value.filter(item => !item.subsector_id).map(item => ({
        'id': item.sector_id,
        'name': item.sector_name
    }))
);

const filteredCombinedList = computed(() => {
    if (!searchFilter.value.length) {
        return combinedList.value;
    }

    let filteredItems = combinedList.value.filter(item => item.name.toLowerCase().includes(searchFilter.value.toLowerCase()));

    return combinedList.value.filter(item =>
        filteredItems.some(filteredItem =>
            (item.subsector_id && filteredItem.subsector_id === item.subsector_id) ||
            (!item.subsector_id && filteredItem.sector_id === item.sector_id)
        )
    );
})

const breadCrumbs = computed(() => {
    const crumbs = ['All'];

    if (currentSector.value) {
        crumbs.push(currentSector.value.name);
    }

    if (currentSubsector.value) {
        crumbs.push(currentSubsector.value.name);
    }

    return crumbs;
})

const addToTrackersTitle = computed(() => {
    if (startupsIds.value.length === 1) {
        return "Add Startup To Trackers";
    } else {
        return `Add ${startupsIds.value.length} Startups To Trackers`;
    }
})

const firstLevel = computed(() => viewLevel.value === 1);

watch(currentSelection, (value) => {
    showSectorSelect.value = false;

    if (value) {
        currentSector.value = {
            id: value.sector_id,
            name: value.sector_name
        }

        if (value.subsector_id) {
            currentSubsector.value = {
                id: value.subsector_id,
                name: value.subsector_name
            }
        } else {
            currentSubsector.value = null;
        }
    } else {
        currentSector.value = null;
        currentSubsector.value = null;
    }
})

watch(viewLevel, value => {
    if (value < 3) {
        currentSubsector.value = null;
        startupsIds.value = [];
    }

    if (value < 2) {
        currentSector.value = null;
        selectedTracker.value = null;
        selectedCorporates.value = [];
        selectedStartups.value = [];
    }
});

watch([currentSector, currentSubsector], () => {
    if (currentSector.value) {
        if (currentSubsector.value) {
            viewLevel.value = 3;
            searchType.value = 'subsector';
            getSubscription();
        } else {
            viewLevel.value = 2;
        }

        currentSelection.value = combinedList.value.find(item => {
            if (currentSubsector.value) {
                return item.subsector_id === currentSubsector.value.id;
            } else {
                return item.sector_id === currentSector.value.id && !item.subsector_id;
            }
        });
    } else {
        viewLevel.value = 1;
        searchType.value = "";
        currentSelection.value = null;
    }
});

watch(showSectorSelect, value => {
    if (value) {
        combinedSelect.value.activate();
    }
});

watch(selectedTracker, value => {
    showTrackerSelect.value = false;

    if (value) {
        viewLevel.value = 3;
        searchType.value = "tracker";
    } else {
        viewLevel.value = 1;
        searchType.value = "";
    }
});

watch(selectedCorporates, value => {
    if (value.length) {
        viewLevel.value = 3;
        searchType.value = "corporates";
    } else {
        viewLevel.value = 1;
        searchType.value = "";
    }
});

watch(selectedStartups, value => {
    if (value.length) {
        viewLevel.value = 3;
        searchType.value = "startups";
    } else {
        viewLevel.value = 1;
        searchType.value = "";
    }
});

onMounted(() => {
    getSectorsList();
});

async function getSectorsList() {
    const {data} = await marketsService.getCombinedSectorsList();
    combinedList.value = data;

    if (sectorId) {
        const sector = combinedList.value.find(item => item.sector_id === parseInt(sectorId) && !item.subsector_id);

        if (sector) {
            currentSector.value = {
                id: sector.sector_id,
                name: sector.sector_name
            };

            viewLevel.value = 2;
        }
    }

    if (clusterId) {
        const subsector = combinedList.value.find(item => item.subsector_id === parseInt(clusterId));

        if (subsector) {
            currentSector.value = {
                id: subsector.sector_id,
                name: subsector.sector_name
            };

            currentSubsector.value = {
                id: subsector.subsector_id,
                name: subsector.subsector_name
            };

            viewLevel.value = 3;
        }
    }
}

async function addStartupsToTrackers(trackers) {
    await trackersCompaniesService.addCompanies(trackers.map(item =>item.id), startupsIds.value);

    Vue.notify({
        group: 'app',
        type: 'success',
        duration: 3000,
        text: getNoun("Startup", startupsIds.value.length) + " Added"
    })
}

async function addStartupToTracker(startupId) {
    const {data} = await startupsService.getTrackersIds(startupId);

    excludeTrackers.value = data;
    startupsIds.value = [startupId];
    addToTrackersModal.value.open();
}

function showSimilar(startupId) {
    similarStartupsModal.value.open(startupId)
}

function showPartnerships(corporate) {
    if (!startupsNodes.value) {
        partnershipsModal.value.open(corporate);
    }
}

function getSectorImage(id) {
    return `${process.env.VUE_APP_API_ENDPOINT}/img/sectors/${id}.jpg`;
}

async function getSubscription() {
    if (currentSubsector.value) {
        const {data} = await marketsService.getSubscription(currentSubsector.value.id);
        hasSubscription.value = data;
    }
}

async function subscribe() {
    const {data} = await marketsService.subscribe(currentSubsector.value.id, !hasSubscription.value);

    if (data) {
        hasSubscription.value = true;

        Vue.notify({
            group: 'app',
            type: 'success',
            duration: 3000,
            text: "Subscribed"
        })
    } else {
        hasSubscription.value = false;

        Vue.notify({
            group: 'app',
            type: 'success',
            duration: 3000,
            text: "Unsubscribed"
        })
    }
}

provide('counts', counts);
provide('startupsIds', startupsIds);
provide('currentSector', currentSector);
provide('currentSubsector', currentSubsector);
provide('selectedCorporates', selectedCorporates);
provide('selectedStartups', selectedStartups);
provide('selectedTracker', selectedTracker);
provide('viewLevel', viewLevel);
</script>

<template>
    <div>
        <div class="charts charts--markets pb-0">
            <div class="chart-row charts--markets__content chart-row--top">
                <div
                    v-if="viewLevel < 3 || searchType === 'subsector'"
                    class="charts--markets__breadcrumbs chart-item-airy"
                    :class="{'selected': viewLevel > 1}"
                    v-click-outside="() => {showSectorSelect = false}"
                >
                    <div v-if="currentSector" class="charts--markets__breadcrumbs-image" :style="{backgroundImage: `url(${getSectorImage(currentSector.id)})`, backgroundSize: 'cover'}"/>
                    <div v-else class="charts--markets__breadcrumbs-image">
                        <!-- <img src="https://sp-images-prod.s3.amazonaws.com/assets/angellist-brands.png" alt="" /> -->
                    </div>
                    <span class="charts--markets__breadcrumbs__item" v-for="(crumb, index) in breadCrumbs" :key="index">
                        <template v-if="index < breadCrumbs.length - 1">
                            <a
                                class="link"
                                @click="viewLevel = index + 1"
                            >
                                {{crumb}}
                            </a>
                        </template>

                        <template v-else>
                            <a
                                v-if="!showSectorSelect"
                                class="link"
                                @click.stop="showSectorSelect = true"
                            >
                                {{crumb}}
                            </a>
                        </template>
                    </span>

                    <Multiselect
                        ref="combinedSelect"
                        v-show="showSectorSelect"
                        v-model="currentSelection"
                        placeholder=""
                        class="multiselect-mid multiselect-with-sub"
                        :options="filteredCombinedList"
                        :internal-search="false"
                        select-label=""
                        deselect-label=""
                        label="name"
                        track-by="name"
                        @search-change="query => searchFilter = query"
                    >
                        <template #option="props">
                            <span v-if="props.option.subsector_id" class="multiselect__sub">{{props.option.subsector_name}}</span>
                            <span v-else>{{props.option.sector_name}}</span>
                        </template>
                    </Multiselect>
                </div>

                <a
                    v-if="firstLevel || searchType === 'corporates'"
                    @click="selectCorporatesModal.open()"
                    class=""
                    :class="{'selected': selectedCorporates.length}"
                >
                    <template v-if="selectedCorporates.length">
                        <span class="chart-item-selected chart-item-selected--enterprise">
                            {{selectedCorporates.length}} {{getNoun('enterprise', selectedCorporates.length)}} selected
                        </span>
                    </template>
                    <template v-else>
                        <span class="chart-item-airy chart-item-airy--enterprise">Enterprises</span>
                    </template>
                </a>

                <a
                    v-if="firstLevel || searchType === 'startups'"
                    @click="selectStartupsModal.open()"
                    class=""
                    :class="{'selected': selectedStartups.length}"
                >
                    <template v-if="selectedStartups.length">
                        <span class="chart-item-selected chart-item-selected--startup">
                            {{selectedStartups.length}} {{getNoun('startup', selectedStartups.length)}} selected
                        </span>
                    </template>
                    <template v-else>
                        <span class="chart-item-airy chart-item-airy--startup">Startups</span>
                    </template>
                </a>

                <div
                    v-if="firstLevel || searchType === 'tracker'"
                    style="max-height: 43px;"
                    v-click-outside="() => {showTrackerSelect = false}"
                >
                    <TrackersSelect
                        v-if="showTrackerSelect"
                        v-model="selectedTracker"
                        :trackers-list="store.getters.trackersList"
                        :multiple="false"
                        class="chart-item-airy chart-item-airy--tracker"
                        :class="{'selected': !!selectedTracker}"
                    />

                    <a
                        v-else
                        @click.stop="showTrackerSelect = true"
                        class="chart-item-airy chart-item-airy--tracker"
                        :class="{'selected': !!selectedTracker}"
                    >
                        <template v-if="selectedTracker">
                            {{selectedTracker.name}}
                        </template>
                        <template v-else>
                            Your trackers
                        </template>
                    </a>
                </div>

                <a
                    v-if="viewLevel > 1"
                    class="chart-item-clear"
                    @click="viewLevel = 1"
                >
                    Clear
                </a>

                <div class="chart__header ml-auto">
                    <div v-if="viewLevel === 3" class="market-switcher">
                        <input type="checkbox" v-model="startupsNodes" class="custom-control-input" id="cci">

                        <label class="custom-control-label" for="cci"></label>
                    </div>
                </div>
            </div>
        </div>

        <div class="charts charts--markets" :class="{'charts--markets--cols': viewLevel === 3}">
            <div class="chart-row charts--markets__content d-block">
                <div class="chart w-2 mb-4">
                    <!--  -->
                    <div class="graph-container" style="padding: 0; position: relative;">
                        <div v-if="viewLevel === 1" class="grid">
                            <div
                                v-for="sector in sectorsList"
                                :key="sector.id"
                                :style="{backgroundImage: `url(${getSectorImage(sector.id)})`}"
                                @click="currentSector = sector"
                                class="graph-container__inner"
                            >
                                {{sector.name}}
                            </div>
                        </div>

                        <PartnershipsMap v-if="viewLevel === 2"/>

                        <PartnershipsGraph
                            v-if="viewLevel === 3"
                            :search-type="searchType"
                            :search-object-id="searchObjectId"
                            :startups-nodes="startupsNodes"
                            @addToTracker="addStartupToTracker"
                            @showSimilar="showSimilar"
                            @showPartnerships="showPartnerships"
                        />
                    </div>
                </div>

                <div class="card-markets" v-show="viewLevel === 3">
                    <header class="card-markets__header">Reports</header>

                    <div class="card-markets__content">
                        <div class="row">
                            <div class="col-12 col-md-6 mb-4 mb-md-0">
                                <h2 class="card-markets__title">Your Reports</h2>

                                <MarketReportsHistory
                                    ref="userReportsHistory"
                                    :for-user="true"
                                    @viewReport="(name) => reportModal.open(name, true)"
                                />
                            </div>

                            <div class="col-12 col-md-6">
                                <h2 class="card-markets__title">Your Team's Reports</h2>

                                <MarketReportsHistory
                                    :for-user="false"
                                    @viewReport="(name) => reportModal.open(name, true)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="charts--markets__sidebar-wrapper">
                <div v-if="currentSubsector || selectedTracker" class="charts--markets__sidebar mb-4">
                    <div class="charts--markets__sidebar__header">
                        <div>
                            <div>
                                <h3 class="heading3">Showing {{counts.partnerships}} partnerships</h3>

                                <div class="link-cols">
                                    <a class="link-startup" @click="startupsModal.open">View All Startups</a>

                                    <a @click="addToTrackersModal.open()" class="link-tracker">Add To Tracker</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="currentSubsector" class="charts--markets__sidebar__inner">
                        <h3 class="heading3">Reports</h3>

                        <ul class="list-markets-reports">
                            <li class="list-markets-reports__partnership">
                                <div>
                                    <h5 class="heading5 heading5--secondary">Sector Partnership Landscape</h5>

                                    <a class="link-arrow" @click="reportModal.open(currentSubsector.name, false)">View Report</a>
                                </div>
                            </li>
                            <!--
                            <li class="list-markets-reports__corporate">
                                <div>
                                    <h5 class="heading5 heading5--secondary">Corporate Engagement</h5>

                                    <a href="#" class="link-arrow">View Report</a>
                                </div>
                            </li>
                            <li class="list-markets-reports__trends">
                                <div>
                                    <h5 class="heading5 heading5--secondary">Partnership Trends & Benchmarks</h5>

                                    <a href="#" class="link-arrow">View Report</a>
                                </div>
                            </li>-->
                        </ul>
                    </div>
                </div>

                <div v-if="viewLevel === 3" class="card-markets">
                    <header class="card-markets__header d-flex align-items-center">
                        Updates

                        <a v-if="currentSubsector" @click="subscribe" class="link-notification ml-auto">
                            <span v-if="hasSubscription" class="link-notification__var link-notification__var--on">
                                Notifications On
                            </span>

                            <span v-else class="link-notification__var link-notification__var--off">
                                Enable Notifications
                            </span>
                        </a>
                    </header>

                    <MarketUpdates
                        :search-type="searchType"
                        :search-object-id="searchObjectId"
                    />
                </div>
            </div>
        </div>

        <!-- <div class="charts--markets">
           <div class="charts--markets__content">
                <div class="card-markets">
                    <header class="card-markets__header">Reports</header>

                    <div class="card-markets__content">
                        <div class="row">
                            <div class="col-12 col-md-6 mb-4 mb-md-0">
                                <h2 class="card-markets__title">Your Reports</h2>

                                <div class="report-placeholder">
                                    Market intelligence reports you generate will display here
                                </div>
                            </div>
                            <div class="col-12 col-md-6">
                                <h2 class="card-markets__title">Your Team's Reports</h2>

                                <div class="report-placeholder report-placeholder__team">
                                    Reports generated by your team will display here
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div> -->

        <MarketStartupsModal
            ref="startupsModal"
        />

        <SimilarStartups
            ref="similarStartupsModal"
        />

        <AddToTrackers
            ref="addToTrackersModal"
            :exclude-trackers-list="excludeTrackers"
            :header="addToTrackersTitle"
            @trackersSelected="addStartupsToTrackers"
        />

        <ReportModal
            ref="reportModal"
            @reportLoaded="userReportsHistory.reportOpened(currentSubsector.name)"
        />

        <PartnershipsModal
            ref="partnershipsModal"
            :search-type="searchType"
            :search-object-id="searchObjectId"
        />

        <SelectCorporatesModal ref="selectCorporatesModal"/>
        <SelectStartupsModal ref="selectStartupsModal"/>
    </div>
</template>

<style scoped>
    @media (min-width: 768px) {
        .graph-container__back {
            position: absolute; 
            top: 16px;
            left: 10px;
            z-index: 2;
        }
    }
    .grid {
        /*display: grid;*/
        /*grid-template-columns: repeat(5, 1fr);*/
        display: flex;
        flex-wrap: wrap;

        div {
            display: flex;
            align-items: center;
            justify-content: center;
            /*height: 150px;*/
            cursor: pointer;
            color: white;
            text-align: center;
            background-size: cover;
            text-transform: uppercase;
            font-size: 12px;
            aspect-ratio: 1 / 1;
            width: calc(100% / 5);
        }
    }
</style>
